import React, { useState } from "react";
import { FileSearch, MapPin, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  number: "01",
  title: "Request Quote",
  description: "Contact us via phone or our online form for a free, no-obligation estimate tailored specifically to your solar system.",
  icon: FileSearch
}, {
  id: 2,
  number: "02",
  title: "Professional Cleaning",
  description: "Our technicians use safe roof methods and specialized equipment to remove debris while ensuring safety first at all times.",
  icon: MapPin
}, {
  id: 3,
  number: "03",
  title: "Maximum Performance",
  description: "Enjoy a cleaner, more efficient system that generates more power and increases your energy savings immediately.",
  icon: FileCheck
}];
export default function AuditProcess() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="16035" id="our-cleaning-process" ref={ref} className="relative w-full py-20 lg:py-32 bg-white text-[hsl(220,40%,20%)] overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[hsl(45,100%,90%)]/40 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-[hsl(200,95%,90%)]/30 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight text-[hsl(220,40%,20%)]">
            Our Cleaning Process
          </h2>
          <p className="text-lg text-[hsl(220,20%,45%)] font-sans leading-relaxed">
            A bright and efficient three-step approach designed to restore your solar panels to their peak performance and maximize your return on investment.
          </p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="hidden lg:block absolute top-12 left-0 w-full h-[2px] bg-slate-100">
            <div className="h-full bg-[hsl(45,100%,50%)] transition-all duration-500 ease-out" style={{
            width: `${activeStep / (steps.length - 1) * 100}%`
          }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => {
            const isActive = index === activeStep;
            const Icon = step.icon;
            return <div key={step.id} data-index={index} onClick={() => setActiveStep(index)} className={cn("group relative flex flex-col p-8 rounded-[1.5rem] transition-all duration-500 cursor-pointer border border-transparent", isActive ? "bg-white shadow-[0_20px_50px_rgba(45,50,60,0.08)] border-slate-100 translate-y-[-8px]" : "bg-transparent hover:bg-[hsl(200,95%,98%)] border-transparent hover:border-slate-100", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
              transitionDelay: `${index * 150}ms`
            }}>
                  {}
                  <div className="flex items-center justify-between mb-6">
                    <span className={cn("font-mono text-4xl font-bold transition-colors duration-300", isActive ? "text-[hsl(45,100%,50%)]" : "text-slate-200 group-hover:text-slate-300")}>
                      {step.number}
                    </span>
                    <div className={cn("p-3 rounded-full transition-all duration-300", isActive ? "bg-[hsl(45,100%,50%)] text-white" : "bg-slate-100 text-slate-400")}>
                      <Icon strokeWidth={1.5} className="w-6 h-6" />
                    </div>
                  </div>

                  {}
                  <h3 className={cn("text-xl font-bold mb-3 transition-colors duration-300", isActive ? "text-[hsl(220,40%,20%)]" : "text-[hsl(220,40%,20%)]/80")}>
                    {step.title}
                  </h3>
                  <p className={cn("text-sm leading-relaxed font-sans transition-colors duration-300", isActive ? "text-[hsl(220,20%,45%)]" : "text-[hsl(220,20%,45%)]/70")}>
                    {step.description}
                  </p>

                  {}
                  <div className={cn("absolute left-0 top-0 bottom-0 w-1 bg-[hsl(45,100%,50%)] rounded-l-[1.5rem] lg:hidden transition-opacity duration-300", isActive ? "opacity-100" : "opacity-0")} />
                </div>;
          })}
          </div>
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Button asChild variant="secondary" size="lg" className="font-bold text-[hsl(220,40%,10%)] bg-[hsl(45,100%,50%)] hover:bg-[hsl(45,100%,50%)]/90 rounded-full px-8 shadow-[0_8px_30px_rgba(255,191,0,0.2)] transition-transform hover:scale-105">
            <Link to="/ContactUsPage">
              Book Your Cleaning
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-[hsl(220,20%,45%)] font-sans">
            Guaranteed response within 24 hours
          </p>
        </div>
      </div>
    </section>;
}